import { createAsyncThunk } from '@reduxjs/toolkit';
import ReportBuilderService from 'services/report-builder.service';
import ReportListingService from 'services/report-listing.service';
import { ReportService } from 'services/report.service';
import { SelectedSection } from 'redux-v2/sidebar-store/sidebar-store.state';
import {
  IBuilder, IRawTemplate,
  IReportBuilderColumn,
  IReportBuilderCustomColumns,
  IReportMetaInfo, IReportMetaInfoV2, ReportBuilderStatus,
  ReportSourceInfo,
  IReportConfig,
} from 'core/models/report-builder/report-builder';
import { IFilterConfig, IPage, ISetDimensionsEntityGroupingPayload } from 'core/models/report-response';
import { IGlobalState } from 'core/models/redux';
import FilterFetchUtility from 'core/utils/filter-fetch-utility';
import { IPanelDataResponse } from 'core/models/dashboard';
import { FeatureContext, PageContext } from 'core/constants/common';
import SieraResponse from 'core/models/siera-response';
import { ColumnModes, DimensionStatus, statusCodes } from 'core/constants/report';
import { setSettingsError } from 'redux-v2/settings-store/settings-store.reducer';
import { isValidArrayAndNotEmpty, isValidObjectAndNotEmpty } from 'components/feature/Report/ReportSidebar/common/helpers';
import { getMeasureIdentifiersPayload } from 'components/feature/ReportBuilderV2/ReportBuilderDetail/DrilldownTabContent/drilldown.util';
import { ObjModel, ReportResponseModel } from 'core/models';
import { VisualizationTypes } from 'core/constants/visualizations';
import { getCreatedByListForReports } from 'redux-v2/customAnalytics/custom-analytics.requests';
import { getDataSourceTitle, isListReport, SubQueryPanelType } from 'core/utils/report-builder.util';
import { getDefaultEntity } from 'core/utils/report.util';
import { getIdVariants, getWrapperRepresentativeFilterId } from 'core/utils/custom-filter-editor.util';
import { IFilterValueChangePayload, IUpdateQueryPayload } from 'core/models/report-builder/report-builder-redux';
import { fetchMetaSelectorOptions } from 'core/utils/entity-grouping.util';
import { buildCustomDimension, getDataTypeFromCustomColumn } from 'core/utils/report-expressions';
import { FilterType } from 'core/constants/filter';
import { IVariables } from 'core/models/store';
import { MySpace } from 'components/feature/Report/ReportSidebar/common/constants';
import {
  DrilldownColumnConfig,
  IFilterDataLoadRequestPayload, MeasureIdentifier, MeasureIdentifierPayload, ReportBuilderActions,
  IRawTemplatePayload,
  ISaveReportPayload,
  IResetReportConfigPayload,
  IDataSourceSavePayload,
  ExpressionContext,
  ICustomExpression,
  UpdateType,
} from './report-builder-store.state';
import {
  clearCustomFilterDefinitions,
  clearFormattingRules,
  getRawTemplateDetailsFailure,
  getRawTemplateDetailsRequest,
  getRawTemplateDetailsSuccess,
  initDrilldownMapping,
  queryBuilderPreviewDataloadStatus,
  queryBuilderPreviewFailureState,
  queryBuilderPreviewSuccessState,
  reportBuilderAddUpdateCustomMeasure,
  reportBuilderFilterValueChange,
  reportBuilderInitialFailureState, reportBuilderInitialLoadState, reportBuilderInitialSuccessState,
  reportBuilderPreviewFailureState, reportBuilderPreviewLoadState, reportBuilderPreviewSuccessState,
  reportBuilderQueryFilterValueChange,
  reportBuilderSetMetaSelectorOptions,
  reportBuilderSetTempId,
  reportBuilderTypeChangeFailure,
  reportBuilderTypeChangeRequest,
  reportBuilderTypeChangeSuccess,
  reportBuilderUpdateSelectedTemplate,
  reportBuilderUpdateVisualization,
  saveDataSourceChangesFailure,
  saveDataSourceChangesRequest,
  saveDataSourceChangesRequestReset,
  saveDataSourceChangesSuccess,
  updateCustomHierarchies,
  updateDataSourceChanges,
  updateParentReportDsSaveStatus,
  updateReportMetadataPostJoinsSave,
  updateSelectedQuery,
} from './report-builder-store.reducer';
import {
  getSaveReportConfigPayload, selectedReportTemplate, getInitialDrilldownConfigInfo, getUpdatedDrilldownCols,
  isNamespaceSelectionModified,
  getNestedQuerySaveReportConfigPayload,
  getBuilderObj,
  getQueryConfigFromRawConfig,
  getUnionConfigFromJoins,
  isUnionJoinType,
  filterBuilderToFilterConfig,
} from './report-builder-store.utils';

export const saveReportConfig = createAsyncThunk(ReportBuilderActions.SAVE_REPORT_CONFIG,
  async (payload:ISaveReportPayload, { rejectWithValue, dispatch }) => {
    try {
      return await new ReportBuilderService().saveReportConfig(payload.data, payload.params);
    } catch (err: any) {
      if (err?.status === statusCodes.Forbidden) {
        dispatch(getCreatedByListForReports(payload.data?.ReportConfigId));
      }
      return rejectWithValue(err);
    }
  });

export const getReportBuilderDynamicFilters = createAsyncThunk(ReportBuilderActions.GET_ADDITIONAL_FILTERS,
  async (reportId:string) => new ReportBuilderService().getDynamicFilters(reportId));

export const getDynamicDimensions = createAsyncThunk(ReportBuilderActions.GET_ADDITIONAL_DIMENSIONS,
  async (reportId:string) => new ReportBuilderService().getDynamicDimensions(reportId));

export const getDynamicMeasures = createAsyncThunk(ReportBuilderActions.GET_ADDITIONAL_MEASURES,
  async (reportId:string) => new ReportBuilderService().getDynamicMeasures(reportId));

export const getFieldsRequest = createAsyncThunk(ReportBuilderActions.GET_FIELDS,
  async (reportId:string) => new ReportBuilderService().getFieldsRequest(reportId));

export const getReportRawConfig = createAsyncThunk(ReportBuilderActions.GET_REPORT_RAW_CONFIG_REQUEST,
  async (reportId:string) => new ReportBuilderService().getReportRawConfig({ reportId }));

export const getReportRenderConfig = createAsyncThunk(ReportBuilderActions.GET_REPORT_RENDER_CONFIG_REQUEST,
  async (params: { reportId: string, variables: IVariables }) => new ReportBuilderService().getReportRenderConfig({ reportId: params.reportId }));

export const getReportBuilderFiltersLoad = createAsyncThunk(ReportBuilderActions.FILTER_DATA_LOAD_REQUEST,
  async (filterpayload: IFilterDataLoadRequestPayload, { getState }) => {
    const {
      filterId,
      variables,
      filterValue,
      isLazyLoad,
      associatedCustomFilterId,
    } = filterpayload;
    const state = getState() as IGlobalState;
    const {
      reportBuilder: {
        filterBuilder: { filterConfig, appliedFilters, customFilterDefinitions },
        updatedReportBuilderInfo: { selectedTemplate: { ReportConfig }, reportBuilderSavePayload: ReportConfigPayload },
        assortedInfo: { reportId },
      },
    } = state;

    let featureContextInfo = ReportConfig?.FeatureContext;
    if (Object.prototype.hasOwnProperty.call(ReportConfig, 'FeatureContext') && !ReportConfig?.FeatureContext) {
      featureContextInfo = FeatureContext.Sales;
    }
    const userContextInfo = ReportConfig?.UserContext;
    let filter = filterConfig.find(
      (item: IFilterConfig) => item.ID === filterId,
    );

    // Fallback for custom-filter constituents: a query-applied constituent may not
    // live in the main report's filterConfig (e.g. the user hasn't visited the
    // subquery so it was never pulled into main config). Search the per-query
    // state instead. Two locations depending on whether the subquery page has
    // been visited:
    //   1. QueryDataInfo.updatedInfo.filterBuilder.filterConfig — populated when
    //      the user has visited the subquery (already in IFilterConfig shape).
    //   2. QueryPayload.Filter.FilterGroup — always present from the saved config;
    //      converted to IFilterConfig shape by hoisting Render-block fields,
    //      mirroring allSubBuilderAppliedFiltersSelector.
    //
    // ID matching: query-side filter IDs carry a `_<queryId>` suffix while the
    // constituent's stored `FilterId` is the bare form. Check both forms per query.
    if (!filter && associatedCustomFilterId) {
      const { DynamicQueryList } = state.reportBuilder.reportQueryBuilder;
      (DynamicQueryList || []).some((query) => {
        const suffixedId = `${filterId}_${query.Id}`;
        const matchesId = (id: string) => id === filterId || id === suffixedId;
        const fromQueryDataInfo = query.QueryDataInfo?.updatedInfo?.filterBuilder?.filterConfig?.find(
          (item: IFilterConfig) => matchesId(item.ID),
        );
        if (fromQueryDataInfo) {
          filter = fromQueryDataInfo;
          return true;
        }
        const fromQueryPayload = query.QueryPayload?.Filter?.FilterGroup?.find(
          (fg) => matchesId(fg.ID),
        );
        if (fromQueryPayload) {
          const {
            Label, Type, DefaultValue, Metadata, DropdownMetaData: renderDropdownMetaData,
          } = fromQueryPayload.Render || {};
          filter = {
            ...fromQueryPayload,
            Label,
            Type,
            DefaultValue,
            Metadata,
            QueryId: query.Id,
            DropdownMetaData: fromQueryPayload.DropdownMetaData || renderDropdownMetaData,
            IsDynamic: true,
          } as IFilterConfig;
          return true;
        }
        return false;
      });
    }
    // Resolve parent filter value. If LinkedTo points to a constituent that is the
    // REPRESENTATIVE constituent (FilterKey, else first linked) of a custom filter, read
    // the value from that wrapper's appliedFilters — the wrapper stands in for that parent.
    // If the parent is merely a non-key constituent, the wrapper no longer represents it
    // (e.g. the FilterKey was moved to another constituent), so the dependency is broken
    // and we must NOT inherit the wrapper's value.
    let value = null;
    // Augment the lookup pool with sub-query (SubBuilder) filters so a dependent filter whose
    // parent lives in a sub-query can be matched. The main filterConfig only carries sub-query
    // filters flagged ShowInMainReport; the rest live in the save payload's SubBuilders
    // (updatedReportBuilderInfo.reportBuilderSavePayload.SubBuilders). Passing isSubBuilder=false
    // to filterBuilderToFilterConfig includes ALL sub-query filters (skips the ShowInMainReport
    // gating) and recurses SubFilters. Scoped to parent-value resolution only.
    const subQueryFilters = (ReportConfigPayload?.SubBuilders || []).flatMap(
      (builder) => filterBuilderToFilterConfig(builder.Filter, [], [], false),
    );
    const parentLookupFilterConfig = isValidArrayAndNotEmpty(subQueryFilters)
      ? [...filterConfig, ...subQueryFilters]
      : filterConfig;
    const isFilterToReloadACustomFilter = filter.IsCustomFilter;
    const filterKeyOfCustomFilterToReload = isFilterToReloadACustomFilter ? filter?.Metadata?.FilterKey : '';
    // If the filter to reload is a custom filter, its corresponding config does not store the LinkedTo key to point to the parent.
    // So, we extract the parent filters Id by looking into the constituent dependent filter's config present in this custom filter.
    const correspondingFilterForFilterKey = isFilterToReloadACustomFilter ? parentLookupFilterConfig?.find((f) => !f.IsCustomFilter && f.Metadata?.FilterKey === filterKeyOfCustomFilterToReload) : ({} as IFilterConfig);

    if (filter.LinkedTo || correspondingFilterForFilterKey?.LinkedTo || correspondingFilterForFilterKey?.Render?.LinkedTo) {
      const linkedFilter = parentLookupFilterConfig.find((item: IFilterConfig) => item.ID === filter.LinkedTo || item.ID === correspondingFilterForFilterKey?.LinkedTo || item.ID === correspondingFilterForFilterKey?.Render?.LinkedTo);
      const parentCfId = linkedFilter?.AssociatedCustomFilterId;
      const parentDef = parentCfId
        ? customFilterDefinitions?.find((d) => d.ID === parentCfId)
        : undefined;
      const representativeId = getWrapperRepresentativeFilterId(parentDef);
      const isRepresentative = !!representativeId && !!linkedFilter
        && getIdVariants(linkedFilter.ID, linkedFilter.QueryId).includes(representativeId);
      const resolvedId = isRepresentative ? parentCfId : filter.LinkedTo;
      // Only break the dependency when the parent IS a custom-filter constituent that is no
      // longer the wrapper's representative (e.g. the FilterKey was moved to another
      // constituent). A plain parent dropdown that was never part of a custom filter has no
      // parentDef and must still cascade its value — otherwise ParentDropDownValues ships empty.
      const isCustomFilterConstituent = !!parentDef;
      if (isCustomFilterConstituent && !isRepresentative && filter.Type !== FilterType.UserMultiSelect) {
        value = null;
      } else {
        value = isValidObjectAndNotEmpty(appliedFilters[resolvedId]) ? appliedFilters[resolvedId]?.FilterResponse : null;
      }
    }
    if (filter.Type === FilterType.UserMultiSelect && value === null && !filter.IsPrimaryUserFilter) {
      value = variables.SER_USER_AUTO_ID; // for getting the autoUserId from auth for sales user flow (as the pre-applied filter is only linked to primary user filter)
    }
    const filterFetchUtility = new FilterFetchUtility({ ...variables, reportId, ExistingCustomColumns: ReportConfig?.Builder?.[0]?.CustomColumns });
    const filterPayload = filterFetchUtility.getUserAdditionalFilterPayload(appliedFilters, filterConfig);

    return filterFetchUtility
      .GetSingleFilterData(
        filter,
        value,
        isLazyLoad ? filterValue : appliedFilters[filterId].FilterResponse,
        appliedFilters[filterId],
        filterPayload,
        featureContextInfo,
        !ReportConfigPayload?.Builder[0]?.Hierarchy?.ToUse,
        false,
        userContextInfo,
      );
  });

export const initialSaveTemplateConfig = createAsyncThunk(ReportBuilderActions.INITIAL_SAVE_TEMPLATE,
  async ({ namespace }: IRawTemplatePayload, { getState, dispatch }) => {
    const { auth: { variables } } = getState() as IGlobalState;
    dispatch(getRawTemplateDetailsRequest());
    new ReportBuilderService()
      .getRawTemplateDetails(namespace)
      .then((response: IRawTemplate) => {
        dispatch(getRawTemplateDetailsSuccess());
        dispatch(reportBuilderUpdateSelectedTemplate(response));

        dispatch(reportBuilderInitialLoadState());
        const reportConfig = selectedReportTemplate(response, response.FilterMetaInfo);

        const updatedReportBuilderPayload = {
          ...reportConfig,
          Status: ReportBuilderStatus.DRAFT,
        };
        if (Object.prototype.hasOwnProperty.call(updatedReportBuilderPayload, 'FeatureContext') && !updatedReportBuilderPayload.FeatureContext) {
          updatedReportBuilderPayload.FeatureContext = FeatureContext.Sales;
        }
        new ReportBuilderService().saveReportConfig(updatedReportBuilderPayload, { PageContext: PageContext.BUILDER }).then((res: SieraResponse<string>) => {
          dispatch(reportBuilderInitialSuccessState({ reportId: res.data, variables }));
          dispatch(getReportBuilderPreviewData({ reportId: res.data, isInitialLoad: true }));
        }).catch(() => dispatch(reportBuilderInitialFailureState()));
      })
      .catch(() => {
        dispatch(getRawTemplateDetailsFailure());
      });
  });

export const getReportBuilderPreviewData = createAsyncThunk(ReportBuilderActions.GET_PREVIEW_DATA,
  async (params:{reportId: string, isInitialLoad?:boolean, initializeDrilldown?:boolean}, { getState, dispatch }) => {
    dispatch(reportBuilderPreviewLoadState());
    const { reportBuilder } = getState() as IGlobalState;
    const { reportEditInfo, updatedReportBuilderInfo: { reportBuilderSavePayload } } = reportBuilder;
    const { reportId, isInitialLoad, initializeDrilldown } = params;
    // For isInitialLoad, use reportBuilderSavePayload directly (already set correctly in SET_INITIAL_SUCCESS_STATE)
    let savePayloadReportConfig: IReportConfig;
    try {
      savePayloadReportConfig = isInitialLoad
        ? reportBuilderSavePayload
        : getSaveReportConfigPayload(reportBuilder);
    } catch (err) {
      dispatch(reportBuilderPreviewFailureState({ error: { ErrorMessage: (err as Error)?.message || 'Unknown error building save payload' } }));
      return;
    }
    const reportType = savePayloadReportConfig?.Builder?.[0]?.Type;
    const isReportListType = isListReport(reportType);
    if (isInitialLoad) { // initial save config is already done, directly we trigger preview request
      new ReportBuilderService()
        .getDynamicDataById(reportId, reportBuilderSavePayload?.FeatureContext || FeatureContext.Sales)
        .then((res: IPanelDataResponse) => {
          dispatch(reportBuilderPreviewSuccessState({ res, isInitialLoad }));
        })
        .catch((e: any) => {
          const errorData = e?.data || e?.response?.data;
          const ErrorMessage = errorData?.ErrorMessage || errorData?.Message || e?.message;
          dispatch(reportBuilderPreviewFailureState({ error: { ErrorMessage } }));
        })
        .finally(() => {
          dispatch(initDrilldownMapping()); // in case fetch-report-v2 breaks, drilldown should get initialised with raw-template data
          dispatch(getDataSourcesList({}));
        });
    } else {
      new ReportBuilderService() // default preview click, save config followed by preview request
        .saveReportConfig({
          ...savePayloadReportConfig,
          Status: ReportBuilderStatus.DRAFT,
          ReportConfigId: reportEditInfo?.isEditing ? reportEditInfo?.temporaryReportId : savePayloadReportConfig?.ReportConfigId,
        }, { PageContext: reportEditInfo?.isEditing ? PageContext.EDIT : PageContext.BUILDER })
        .then((response: SieraResponse<string>) => {
          // getSaveReportConfigPayload reconciled the funnel's dimension / measure against the
          // current applied columns, but the builder store still holds the pre-reconciliation ids.
          // Sync the reconciled Visualization back into the store on save success so the store, the
          // persisted config and the preview render all reference the same (valid) columns.
          if (savePayloadReportConfig?.Visualization?.Type === VisualizationTypes.FunnelChart) {
            dispatch(reportBuilderUpdateVisualization({
              type: savePayloadReportConfig.Visualization.Type,
              attributes: savePayloadReportConfig.Visualization.Builder?.Properties,
            }));
          }
          if (reportEditInfo?.isEditing) {
            dispatch(reportBuilderSetTempId(response.data));
          }
          new ReportBuilderService()
            .getDynamicDataById(reportEditInfo?.isEditing ? response.data : reportId, reportBuilderSavePayload?.FeatureContext || FeatureContext.Sales)
            .then((res: IPanelDataResponse) => {
              if (initializeDrilldown) {
                dispatch(initDrilldownMapping());
                if (reportEditInfo?.isEditing && !isReportListType) {
                  dispatch(getDrilldownConfig(reportId));
                }
              }
              dispatch(reportBuilderPreviewSuccessState({ res }));
            })
            .catch((e: any) => {
              const errorData = e?.data || e?.response?.data;
              const ErrorMessage = errorData?.ErrorMessage || errorData?.Message || e?.message;
              dispatch(reportBuilderPreviewFailureState({ error: { ErrorMessage } }));
            });
        })
        .catch((err: any) => {
          const errorData = err?.data || err?.response?.data;
          const ErrorMessage = errorData?.ErrorMessage || errorData?.Message || err?.message;
          if ((err?.status || err?.response?.status) === statusCodes.Unprocessable) dispatch(setSettingsError(true));
          dispatch(reportBuilderPreviewFailureState({ error: { ErrorMessage } }));
        });
    }
  });
export const getAdditionalDrilldownColumnsRequest = createAsyncThunk(ReportBuilderActions.GET_ADDITIONAL_DRILLDOWN_COLUMN,
  async ({ reportId, data }:{reportId: string, data: MeasureIdentifier, groupId: string}) => new ReportBuilderService().getAdditionalDrilldownColumns(reportId, data));

export const getAdditionalListReportColumnsRequest = createAsyncThunk(ReportBuilderActions.GET_LIST_REPORTS_ADDITIONAL_COLUMNS_FOR_BUILDER,
  async (reportId:string) => new ReportBuilderService().getAdditionalListReportColumns(reportId));

export const getDefaultDrilldownColumnsRequest = createAsyncThunk(ReportBuilderActions.GET_DEFAULT_DRILLDOWN_COLUMN,
  async ({ reportId, data }:{reportId: string, data: MeasureIdentifierPayload}) => new ReportBuilderService().getDefaultDrilldownColumns(reportId, data));

export const getDrilldownConfig = createAsyncThunk(ReportBuilderActions.GET_DRILLDOWN_CONFIG,
  async (reportId: string) => new ReportBuilderService().getDrilldownConfig(reportId));

export const saveDrilldownColumnConfig = createAsyncThunk(ReportBuilderActions.SAVE_DRILLDOWN_CONFIG,
  async ({ reportId, data }: { reportId: string, data: DrilldownColumnConfig }) => new ReportBuilderService().saveDrilldownColumnConfig(reportId, data));

export const saveDrilldownColsWithSaveReportConfig = createAsyncThunk(ReportBuilderActions.SAVE_DRILLDOWNCOLS_WITH_REPORT_CONFIG,
  async (_, { getState, dispatch }) => {
    const state = getState() as IGlobalState;
    const {
      reportBuilder: {
        updatedReportBuilderInfo: {
          reportBuilderSavePayload, drilldownConfigInfo, appliedMeasures, selectedTemplate,
        }, assortedInfo: { reportId }, reportBuilderInfo: { fields },
      },
    } = state;
    if (drilldownConfigInfo?.enableDrilldown) {
      const defaultEntity = getDefaultEntity(selectedTemplate?.ReportConfig);
      const accordKeys = isValidObjectAndNotEmpty(drilldownConfigInfo.drilldownConfigMapping) ? Object.keys(drilldownConfigInfo.drilldownConfigMapping) : [];
      const columnKeys = isValidObjectAndNotEmpty(drilldownConfigInfo.drilldownColumnConfig) ? Object.keys(drilldownConfigInfo.drilldownColumnConfig) : [];
      const nonExistColumns = accordKeys?.filter((key) => !columnKeys.includes(key));
      const isMeasureWithoutDrilldownCols = accordKeys?.every((key) => drilldownConfigInfo.drilldownConfigMapping[key]?.length > 0);
      if (nonExistColumns?.length && isMeasureWithoutDrilldownCols) {
        const MeasureIdentifiersPayload = getMeasureIdentifiersPayload(nonExistColumns, appliedMeasures, drilldownConfigInfo?.drilldownConfigMapping, fields, defaultEntity);
        new ReportBuilderService().getDefaultDrilldownColumns(reportBuilderSavePayload.ReportConfigId, { DefaultDrillDownColumnsInput: MeasureIdentifiersPayload })
          .then((res: ObjModel.Obj) => {
            const drilldownColConfig = getInitialDrilldownConfigInfo(res, drilldownConfigInfo);
            const updatedData = getUpdatedDrilldownCols(drilldownColConfig);
            dispatch(saveDrilldownColumnConfig({ reportId, data: updatedData }));
          }).catch((e) => console.log(e)); // handle error state
      } else {
        const updatedData = getUpdatedDrilldownCols(drilldownConfigInfo.drilldownColumnConfig);
        dispatch(saveDrilldownColumnConfig({ reportId, data: updatedData }));
      }
    }
  });

export const getAdditionalListReportColumnsRequestForNamespaces = createAsyncThunk(ReportBuilderActions.GET_LIST_REPORTS_ADDITIONAL_COLUMNS_FOR_SUBBUILDER,
  async ({ joinsData, primaryNamespace }: IResetReportConfigPayload, { getState }) => {
    const { reportBuilder } = getState() as IGlobalState;
    const updatedReportConfig = getNestedQuerySaveReportConfigPayload(reportBuilder);
    const isUnionType = isUnionJoinType(joinsData);
    const builderObj = {
      ...updatedReportConfig.Builder[0],
      Joins: joinsData,
      UnionConfig: isUnionType ? getUnionConfigFromJoins(joinsData, primaryNamespace) : null,
      Namespace: primaryNamespace,
    } as IBuilder;
    const payload = getBuilderObj(reportBuilder, builderObj, updatedReportConfig, true);
    const response = await new ReportBuilderService().getAdditionalListReportColumnsForNamespaces(payload as IReportMetaInfoV2);
    return response;
  });

export const getReportBuilderDynamicFiltersForNamespaces = createAsyncThunk(ReportBuilderActions.GET_ADDITIONAL_FILTERS_FOR_NAMESPACES,
  async ({ joinsData, primaryNamespace }: IResetReportConfigPayload, { getState }) => {
    const { reportBuilder } = getState() as IGlobalState;
    const updatedReportConfig = getNestedQuerySaveReportConfigPayload(reportBuilder);
    const isUnionType = isUnionJoinType(joinsData);
    const builderObj = {
      ...updatedReportConfig.Builder[0],
      Joins: joinsData,
      UnionConfig: isUnionType ? getUnionConfigFromJoins(joinsData, primaryNamespace) : null,
      Namespace: primaryNamespace,
    } as IBuilder;
    const payload = getBuilderObj(reportBuilder, builderObj, updatedReportConfig, true);
    const response = await new ReportBuilderService().getDynamicFiltersForNamespaces(payload as IReportMetaInfoV2);
    return response;
  });

export const getDynamicDimensionsForNamespaces = createAsyncThunk(ReportBuilderActions.GET_ADDITIONAL_DIMENSIONS_FOR_NAMESPACES,
  async ({ joinsData, primaryNamespace }: IResetReportConfigPayload, { getState }) => {
    const { reportBuilder } = getState() as IGlobalState;
    const updatedReportConfig = getNestedQuerySaveReportConfigPayload(reportBuilder);
    const isUnionType = isUnionJoinType(joinsData);
    const builderObj = {
      ...updatedReportConfig.Builder[0],
      Joins: joinsData,
      UnionConfig: isUnionType ? getUnionConfigFromJoins(joinsData, primaryNamespace) : null,
      Namespace: primaryNamespace,
    } as IBuilder;
    const payload = getBuilderObj(reportBuilder, builderObj, updatedReportConfig, true);
    const response = await new ReportBuilderService().getDynamicDimensionsForNamespaces(payload as IReportMetaInfoV2);
    return response;
  });

export const getDynamicMeasuresForNamespaces = createAsyncThunk(ReportBuilderActions.GET_ADDITIONAL_MEASURES_FOR_NAMESPACES,
  async ({ joinsData, primaryNamespace }: IResetReportConfigPayload, { getState }) => {
    const { reportBuilder } = getState() as IGlobalState;
    const updatedReportConfig = getNestedQuerySaveReportConfigPayload(reportBuilder);
    const isUnionType = isUnionJoinType(joinsData);
    const builderObj = {
      ...updatedReportConfig.Builder[0],
      Joins: joinsData,
      UnionConfig: isUnionType ? getUnionConfigFromJoins(joinsData, primaryNamespace) : null,
      Namespace: primaryNamespace,
    } as IBuilder;
    const payload = getBuilderObj(reportBuilder, builderObj, updatedReportConfig, true);
    const response = await new ReportBuilderService().getDynamicMeasuresForNamespaces(payload as IReportMetaInfoV2);
    return response;
  });

export const getFieldsRequestForNamespaces = createAsyncThunk(ReportBuilderActions.GET_ADDITIONAL_FIELDS_FOR_NAMESPACES,
  async ({ joinsData, primaryNamespace }: IResetReportConfigPayload, { getState }) => {
    const { reportBuilder } = getState() as IGlobalState;
    const updatedReportConfig = getNestedQuerySaveReportConfigPayload(reportBuilder);
    const isUnionType = isUnionJoinType(joinsData);
    const builderObj = {
      ...updatedReportConfig.Builder[0],
      Joins: joinsData,
      UnionConfig: isUnionType ? getUnionConfigFromJoins(joinsData, primaryNamespace) : null,
      Namespace: primaryNamespace,
    } as IBuilder;
    const payload = getBuilderObj(reportBuilder, builderObj, updatedReportConfig, true);
    const response = await new ReportBuilderService().getFieldsRequestForNamespaces(payload as IReportMetaInfoV2);
    return response;
  });

export const getDataSourcesList = createAsyncThunk(
  ReportBuilderActions.GET_DATA_SOURCES_REQUEST,
  async (params?: { reportId?: string; pageContext?: PageContext }) => new ReportBuilderService().getDataSourcesList(params?.reportId, params?.pageContext),
);
export const getDataSourcesFieldList = createAsyncThunk(
  ReportBuilderActions.GET_DATA_SOURCES_FIELDS_REQUEST,
  async (params: { dataSourceId: string, reportId?: string; pageContext?: PageContext }) => new ReportBuilderService().getDataSourcesFieldList(params.dataSourceId, params?.reportId, params?.pageContext),
);

export const saveNestedJoinsConfig = createAsyncThunk(
  ReportBuilderActions.SAVE_NESTED_JOINS_CONFIG,
  async ({
    joinsData, primaryNamespace, pageContext, updateType,
  }: IResetReportConfigPayload, { getState }) => {
    const { reportBuilder } = getState() as IGlobalState;
    let updatedReportConfig = null;
    if (pageContext === PageContext.EDIT) {
      updatedReportConfig = getQueryConfigFromRawConfig(reportBuilder);
    } else {
      updatedReportConfig = getNestedQuerySaveReportConfigPayload(reportBuilder);
    }

    const isUnionType = isUnionJoinType(joinsData);
    const builderObj = {
      ...updatedReportConfig.Builder[0],
      Joins: joinsData,
      UnionConfig: isUnionType ? getUnionConfigFromJoins(joinsData, primaryNamespace) : null,
      Namespace: primaryNamespace,
    } as IBuilder;
    const payload = getBuilderObj(reportBuilder, builderObj, updatedReportConfig, false, pageContext, updateType);

    const response = await new ReportBuilderService().saveNestedConfigChanges(pageContext, payload as IReportMetaInfo, updateType);
    return response;
  },
);

export const resetReportConfig = createAsyncThunk(ReportBuilderActions.RESET_REPORT_CONFIG_FOR_DATA_SOURCE,
  async ({
    joinsData, primaryNamespace, originalReportId, force, updateType,
  }:IResetReportConfigPayload, { getState, dispatch }) => {
    const { reportBuilder, auth: { variables } } = getState() as IGlobalState;
    const { reportEditInfo, updatedReportBuilderInfo: { reportBuilderSavePayload: reportConfig }, dataSourceInfo: { dataSources } } = reportBuilder;
    const { Details: { Title, Description } } = reportConfig;
    const updatedNamespaceList = getDataSourceTitle(primaryNamespace, joinsData, dataSources, true) as string[];
    const currentNamespaceList = getDataSourceTitle(reportConfig.Builder[0].Namespace, reportConfig.Builder[0].Joins, dataSources, true) as string[];
    const hasPrimaryNamespaceChanged = reportConfig.Builder[0].Namespace !== primaryNamespace;
    const hasSubQuery = reportConfig?.SubBuilders?.length > 0;
    const shouldReportConfigReset = hasSubQuery || hasPrimaryNamespaceChanged || isNamespaceSelectionModified(updatedNamespaceList, currentNamespaceList) || force;
    if (shouldReportConfigReset) {
      const updatedReportConfig = getNestedQuerySaveReportConfigPayload(reportBuilder);

      const reportTempIdForEditFlow = reportEditInfo.temporaryReportId;

      const isUnionType = isUnionJoinType(joinsData);
      const builderObj = {
        ...updatedReportConfig.Builder[0],
        Joins: joinsData,
        UnionConfig: isUnionType ? getUnionConfigFromJoins(joinsData, primaryNamespace) : null,
        Namespace: primaryNamespace,
      } as IBuilder;

      updatedReportConfig.Builder[0] = builderObj;
      const isComboChart = updatedReportConfig.Visualization?.Type === VisualizationTypes.ComboChart;
      const existingBuilder = updatedReportConfig.Visualization?.Builder as ReportResponseModel.IChartVisualizationBuilder;
      const payload = {
        NewDataSources: updatedNamespaceList,
        ReportConfig: {
          ...updatedReportConfig,
          Visualization: {
            ...updatedReportConfig.Visualization,
            ...(isComboChart ? { Type: VisualizationTypes.LineChart } : {}),
            Builder: {
              ...existingBuilder,
              ...(isComboChart ? { ComboConfig: undefined, XAxis: undefined, YAxis: undefined } : {}),
            },
            LinkedReportsConfig: [],
          },
        },
      } as IDataSourceSavePayload;

      dispatch(saveDataSourceChangesRequest());
      new ReportBuilderService()
        .saveDataSourceChanges(reportConfig.SourceInfo.Id, payload, updateType)
        .then((response: IRawTemplate) => {
          dispatch(updateParentReportDsSaveStatus(true));
          dispatch(force ? saveDataSourceChangesRequestReset() : saveDataSourceChangesSuccess());
          dispatch(getRawTemplateDetailsSuccess());
          dispatch(updateReportMetadataPostJoinsSave({ Title, Description, originalReportId }));
          const updatedResponse = {
            ...response,
            ReportConfig: {
              ...response.ReportConfig,
              SourceInfo: {
                ...response.ReportConfig.SourceInfo,
                Id: response.ReportConfig.ReportConfigId,
                Type: ReportSourceInfo.Report,
              },
            },
          };

          dispatch(reportBuilderUpdateSelectedTemplate(updatedResponse));
          dispatch(reportBuilderInitialLoadState());
          dispatch(updateCustomHierarchies(null)); // clear custom hierarchies on report config reset
          dispatch(clearFormattingRules()); // clear conditional formatting rules on report config reset
          dispatch(clearCustomFilterDefinitions()); // clear custom filter definitions on report config reset
          const reportConfigData = selectedReportTemplate(updatedResponse, updatedResponse.FilterMetaInfo);
          const updatedReportBuilderPayload = {
            ...reportConfigData,
            ReportConfigId: reportEditInfo?.isEditing ? reportTempIdForEditFlow : reportConfigData.ReportConfigId,
            Status: ReportBuilderStatus.DRAFT,
          };
          if (Object.prototype.hasOwnProperty.call(updatedReportBuilderPayload, 'FeatureContext') && !updatedReportBuilderPayload.FeatureContext) {
            updatedReportBuilderPayload.FeatureContext = FeatureContext.Sales;
          }
          new ReportBuilderService().saveReportConfig(updatedReportBuilderPayload, { PageContext: reportEditInfo?.isEditing ? PageContext.EDIT : PageContext.BUILDER }).then((res: SieraResponse<string>) => {
            dispatch(reportBuilderInitialSuccessState({ reportId: res.data, variables }));
            dispatch(getReportBuilderPreviewData({ reportId: res.data, isInitialLoad: true }));
            dispatch(getFieldsRequest(res.data));
            dispatch(getDynamicDimensions(res.data));
            dispatch(getDynamicMeasures(res.data));
            dispatch(updateReportMetadataPostJoinsSave({ Title, Description, originalReportId }));
            dispatch(initDrilldownMapping()); // re-initialise drilldown columns according to new measures created
            dispatch(getReportBuilderDynamicFilters(res.data));

            const customColumns = updatedReportBuilderPayload?.Builder?.[0]?.CustomColumns;
            if (isValidArrayAndNotEmpty(customColumns)) {
              customColumns.forEach((customColumn: IReportBuilderColumn) => {
                const customExpression: ICustomExpression = {
                  displayName: customColumn.Name,
                  jsonExpression: customColumn.Props.Dimension.Expression,
                  Mode: ColumnModes.NotApplied,
                  uniqueId: customColumn.Id,
                  Formatter: customColumn.Props.Formatter,
                  DataType: getDataTypeFromCustomColumn(customColumn.Props.Dimension.Expression),
                  dimensionType: customColumn.Props.Dimension.DimensionMode,
                  ExpressionType: ExpressionContext.Custom,
                };
                dispatch(reportBuilderAddUpdateCustomMeasure({ customExpression }));
              });
            }
          }).catch(() => dispatch(reportBuilderInitialFailureState()));
        })
        .catch(() => {
          dispatch(getRawTemplateDetailsFailure());
          dispatch(saveDataSourceChangesFailure());
        });
    } else {
      // manually update the config to include the new joins array and primary namespace details, no need to reset anything
      dispatch(updateDataSourceChanges({ joinsData, primaryNamespace }));
    }
  });

export const resetReportConfigForReportTypeToggle = createAsyncThunk(ReportBuilderActions.RESET_REPORT_CONFIG_FOR_REPORT_TYPE_TOGGLE,
  async ({
    joinsData, primaryNamespace, originalReportId,
  }:IResetReportConfigPayload, { getState, dispatch }) => {
    const { reportBuilder, auth: { variables } } = getState() as IGlobalState;
    const { reportEditInfo, updatedReportBuilderInfo: { reportBuilderSavePayload: reportConfig }, dataSourceInfo: { dataSources } } = reportBuilder;
    const { Details: { Title, Description } } = reportConfig;
    const updatedNamespaceList = getDataSourceTitle(primaryNamespace, joinsData, dataSources, true) as string[];
    const updatedReportConfig = getNestedQuerySaveReportConfigPayload(reportBuilder);
    const reportTempIdForEditFlow = reportEditInfo.temporaryReportId;
    const isUnionType = isUnionJoinType(joinsData);
    const builderObj = {
      ...updatedReportConfig.Builder[0],
      Joins: joinsData,
      UnionConfig: isUnionType ? getUnionConfigFromJoins(joinsData, primaryNamespace) : null,
      Namespace: primaryNamespace,
    } as IBuilder;
    updatedReportConfig.Builder[0] = builderObj;
    const isComboChart = updatedReportConfig.Visualization?.Type === VisualizationTypes.ComboChart;
    const existingBuilder = updatedReportConfig.Visualization?.Builder as ReportResponseModel.IChartVisualizationBuilder;

    const payload = {
      NewDataSources: updatedNamespaceList,
      ReportConfig: {
        ...updatedReportConfig,
        Visualization: {
          ...updatedReportConfig.Visualization,
          ...(isComboChart ? { Type: VisualizationTypes.LineChart } : {}),
          Builder: {
            ...existingBuilder,
            ...(isComboChart ? { ComboConfig: undefined, XAxis: undefined, YAxis: undefined } : {}),
          },
          LinkedReportsConfig: [],
        },
      },
    } as IDataSourceSavePayload;

    dispatch(reportBuilderTypeChangeRequest());
    new ReportBuilderService()
      .saveDataSourceChanges(reportConfig.SourceInfo.Id, payload)
      .then((response: IRawTemplate) => {
        dispatch(reportBuilderTypeChangeSuccess());
        dispatch(updateParentReportDsSaveStatus(true));
        dispatch(updateReportMetadataPostJoinsSave({ Title, Description, originalReportId }));
        const updatedResponse = {
          ...response,
          ReportConfig: {
            ...response.ReportConfig,
            SourceInfo: {
              ...response.ReportConfig.SourceInfo,
              Id: response.ReportConfig.ReportConfigId,
              Type: ReportSourceInfo.Report,
            },
          },
        };
        dispatch(reportBuilderUpdateSelectedTemplate(updatedResponse));
        dispatch(reportBuilderInitialLoadState());
        dispatch(clearCustomFilterDefinitions());
        const reportConfigData = selectedReportTemplate(updatedResponse, updatedResponse.FilterMetaInfo);
        const updatedReportBuilderPayload = {
          ...reportConfigData,
          ReportConfigId: reportEditInfo?.isEditing ? reportTempIdForEditFlow : reportConfigData.ReportConfigId,
          Status: ReportBuilderStatus.DRAFT,
        };
        if (Object.prototype.hasOwnProperty.call(updatedReportBuilderPayload, 'FeatureContext') && !updatedReportBuilderPayload.FeatureContext) {
          updatedReportBuilderPayload.FeatureContext = FeatureContext.Sales;
        }
        new ReportBuilderService().saveReportConfig(updatedReportBuilderPayload, { PageContext: reportEditInfo?.isEditing ? PageContext.EDIT : PageContext.BUILDER }).then((res: SieraResponse<string>) => {
          dispatch(reportBuilderInitialSuccessState({ reportId: res.data, variables }));
          dispatch(getReportBuilderPreviewData({ reportId: res.data, isInitialLoad: true }));
          dispatch(getFieldsRequest(res.data));
          dispatch(getAdditionalListReportColumnsRequest(res.data));
          dispatch(updateReportMetadataPostJoinsSave({ Title, Description, originalReportId }));
          dispatch(getReportBuilderDynamicFilters(res.data));

          /**
           * when we update the custom column in list report, in the process the expression store get cleared. So we need to re-add the custom columns to the expression store.
           * */
          const customColumns = updatedReportBuilderPayload?.Builder?.[0]?.CustomColumns;
          if (isValidArrayAndNotEmpty(customColumns)) {
            customColumns.forEach((customColumn: IReportBuilderColumn) => {
              const customExpression: ICustomExpression = {
                displayName: customColumn.Name,
                jsonExpression: customColumn.Props.Dimension.Expression,
                Mode: ColumnModes.NotApplied,
                uniqueId: customColumn.Id,
                Formatter: customColumn.Props.Formatter,
                DataType: getDataTypeFromCustomColumn(customColumn.Props.Dimension.Expression),
                dimensionType: customColumn.Props.Dimension.DimensionMode,
                ExpressionType: ExpressionContext.Custom,
              };
              dispatch(reportBuilderAddUpdateCustomMeasure({ customExpression }));
            });
          }
        }).catch(() => dispatch(reportBuilderInitialFailureState()));
      })
      .catch(() => {
        dispatch(getRawTemplateDetailsFailure());
        dispatch(reportBuilderTypeChangeFailure());
      });
  });

export const getSubQueryFiltersLoad = createAsyncThunk(ReportBuilderActions.SUB_QUERY_FILTER_LOAD_DATA_REQUEST,
  async (filterpayload: IFilterDataLoadRequestPayload, { getState }) => {
    const {
      filterId,
      variables,
      filterValue,
      isLazyLoad,
    } = filterpayload;
    const {
      reportBuilder: {
        reportQueryBuilder: { SelectedQuery, SelectedQueryData: { updatedInfo: { filterBuilder: { filterConfig, appliedFilters }, ExpressionStore } }, DynamicQueryList },
        updatedReportBuilderInfo: { selectedTemplate: { ReportConfig }, reportBuilderSavePayload: ReportConfigPayload },

      },
    } = getState() as IGlobalState;
    const selectedQuery = DynamicQueryList.find((query) => query.Id === SelectedQuery);
    const selectedQueryConfig = selectedQuery?.QueryConfig;
    const FetchAllSubordinates = !selectedQueryConfig?.ReportConfig?.Builder[0]?.Hierarchy?.ToUse;

    let featureContextInfo = ReportConfig?.FeatureContext;
    if (Object.prototype.hasOwnProperty.call(ReportConfig, 'FeatureContext') && !ReportConfig?.FeatureContext) {
      featureContextInfo = FeatureContext.Sales;
    }
    const userContextInfo = ReportConfig?.UserContext;
    const filter = filterConfig.find(
      (item: IFilterConfig) => item.ID === filterId,
    );
    // Resolve parent filter value. If LinkedTo points to a constituent filter (part of a custom filter),
    // read the value from the parent custom filter's appliedFilters instead.
    let value = null;
    if (filter.LinkedTo) {
      const linkedFilter = filterConfig.find((item: IFilterConfig) => item.ID === filter.LinkedTo);
      const resolvedId = linkedFilter?.AssociatedCustomFilterId || filter.LinkedTo;
      value = isValidObjectAndNotEmpty(appliedFilters[resolvedId]) ? appliedFilters[resolvedId]?.FilterResponse : null;
    }
    if (filter.Type === FilterType.UserMultiSelect && value === null && !filter.IsPrimaryUserFilter) {
      value = variables.SER_USER_AUTO_ID; // for getting the autoUserId from auth for sales user flow (as the pre-applied filter is only linked to primary user filter)
    }
    /**
     * We need to pass the existing custom columns to the filter fetch utility to avoid the stale custom columns.
     * This helps in fetching the filter values for any newly created/updated custom columns that have not been saved yet in backend.
     */
    const existingCustomColumns: IReportBuilderCustomColumns[] = [];
    if (isValidObjectAndNotEmpty(ExpressionStore)) {
      Object.values(ExpressionStore).forEach((customExp: ICustomExpression) => {
        const {
          Name, Id, BuilderConfig, Props,
        } = buildCustomDimension(customExp.displayName, customExp.Mode, customExp.jsonExpression, customExp.uniqueId, customExp.DataType, customExp?.dimensionType, customExp?.description);
        const updatedCustomColumn = {
          Name, Id, BuilderConfig, Props,
        } as IReportBuilderCustomColumns;

        updatedCustomColumn.Props = { ...updatedCustomColumn.Props, Dimension: { ...updatedCustomColumn.Props.Dimension, Applied: customExp.Mode === ColumnModes.NotApplied ? DimensionStatus.NotApplied : DimensionStatus.Applied } };

        existingCustomColumns.push(updatedCustomColumn);
      });
    }
    const filterFetchUtility = new FilterFetchUtility({ ...variables, ExistingCustomColumns: existingCustomColumns });
    const filterPayload = filterFetchUtility.getUserAdditionalFilterPayload(appliedFilters, filterConfig);

    return filterFetchUtility
      .GetSingleFilterData(
        filter,
        value,
        isLazyLoad ? filterValue : appliedFilters[filterId].FilterResponse,
        appliedFilters[filterId],
        filterPayload,
        featureContextInfo,
        FetchAllSubordinates,
        false,
        userContextInfo,
      );
  });

export const getSubQueryPreviewData = createAsyncThunk(ReportBuilderActions.GET_SUB_QUERY_PREVIEW_DATA, async (params:{queryId: string}, { getState, dispatch }) => {
  const { queryId } = params;

  dispatch(queryBuilderPreviewDataloadStatus({ queryId }));
  const { reportBuilder } = getState() as IGlobalState;
  const { updatedReportBuilderInfo: { reportBuilderSavePayload } } = reportBuilder;
  const savePayloadReportConfig = getNestedQuerySaveReportConfigPayload(reportBuilder);
  const paging: IPage = {
    PageIndex: 1,
    PageSize: 10,
  };
  savePayloadReportConfig.Paging = paging;
  new ReportBuilderService() // default preview click, save config followed by preview request
    .saveReportConfig({
      ...savePayloadReportConfig,
      Status: ReportBuilderStatus.PREVIEWQUERY,
      ReportConfigId: '',
    }, { PageContext: PageContext.BUILDER })
    .then((response) => {
      new ReportBuilderService()
        .getDynamicDataById(response.data, reportBuilderSavePayload?.FeatureContext || FeatureContext.Sales)
        .then((res: IPanelDataResponse) => {
          dispatch(queryBuilderPreviewSuccessState({ res }));
        })
        .catch((e: any) => {
          const errorData = e?.data || e?.response?.data;
          const ErrorMessage = errorData?.ErrorMessage || errorData?.Message || e?.message;
          dispatch(queryBuilderPreviewFailureState({ error: { ErrorMessage } }));
        });
    })
    .catch((err: any) => {
      const errorData = err?.data || err?.response?.data;
      const ErrorMessage = errorData?.ErrorMessage || errorData?.Message || err?.message;
      if ((err?.status || err?.response?.status) === statusCodes.Unprocessable) dispatch(setSettingsError(true));
      dispatch(queryBuilderPreviewFailureState({ error: { ErrorMessage } }));
    });
});
export const handleFilterValueChange = createAsyncThunk(ReportBuilderActions.FILTER_VALUE_CHANGE, async (params: { payload: IFilterValueChangePayload }, { getState, dispatch }) => {
  const { reportBuilder } = getState() as IGlobalState;
  const { filterBuilder } = reportBuilder;
  const { payload } = params;
  const { filterId, filterValue, variables } = payload;
  dispatch(reportBuilderFilterValueChange({ filterId, filterValue, variables }));
  const selectedFilterConfig = filterBuilder.filterConfig.find(
    (item: IFilterConfig) => item.ID === filterId,
  );

  if (selectedFilterConfig.QueryId) {
    dispatch(reportBuilderQueryFilterValueChange(payload));
  }
});

export const getAllReportsForLinkedInsights = createAsyncThunk(
  ReportBuilderActions.GET_ALL_REPORTS_FOR_LINKED_INSIGHTS,
  async () => new ReportListingService().getAllReports(SelectedSection.All, MySpace.Id, ''),
);

export const getReportConfigForLinkedInsights = createAsyncThunk(
  ReportBuilderActions.GET_REPORT_CONFIG_FOR_LINKED_INSIGHTS,
  async (reportId: string) => new ReportService().getReportConfig({ reportId }),
);

export const getMetaSelectorOptions = createAsyncThunk(ReportBuilderActions.GET_META_SELECTOR_OPTIONS,
  async (params: ISetDimensionsEntityGroupingPayload, { dispatch }) => {
    const response = await fetchMetaSelectorOptions(params);
    dispatch(reportBuilderSetMetaSelectorOptions({
      res: response,
      data: params.data,
      isCreate: params.mode !== undefined,
      activeId: params.activeId,
    }));
    return response;
  });
